
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Bot } from "lucide-react";
import { Helmet } from "react-helmet";
import { useEffect, useState } from 'react';
import NavigationButtons from '@/components/NavigationButtons';
import { WaitlistDialog } from '@/components/WaitlistDialog';
import RelatedLinks from '@/components/RelatedLinks';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [slug]);

  const post = {
    title: "Deep Seek AI Launches Revolutionary Language Model",
    date: "February 7, 2025",
    author: "Deep Seek Team",
    content: `Deep Seek AI is proud to announce the launch of our latest language model, setting new standards in natural language processing and understanding. This breakthrough represents a significant step forward in AI technology, offering unprecedented capabilities in text generation, comprehension, and analysis.

    Our new model demonstrates exceptional performance across a wide range of tasks, from complex problem-solving to creative writing. It has been trained on a diverse dataset, ensuring broad knowledge coverage and robust performance across different domains.

    Key features of the new model include:
    - Enhanced contextual understanding
    - Improved logical reasoning capabilities
    - Better handling of complex instructions
    - More natural and coherent responses
    - Reduced hallucination and improved factual accuracy

    This release marks a significant milestone in our mission to advance AI technology and make it more accessible to developers and businesses worldwide. We're excited to see how our community will leverage these new capabilities to create innovative solutions and push the boundaries of what's possible with AI.

    Stay tuned for more updates and detailed documentation on how to integrate and maximize the potential of our new language model in your applications.`
  };

  // Schema.org JSON-LD data for the blog post
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "datePublished": "2025-02-07T08:00:00+00:00",
    "dateModified": "2025-02-07T09:30:00+00:00",
    "author": {
      "@type": "Organization",
      "name": "Deep Seek AI",
      "url": "https://deepseek.ai"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Deep Seek AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://deepseek.ai/logo.png"
      }
    },
    "description": "Deep Seek AI launches a revolutionary language model with enhanced contextual understanding and improved logical reasoning capabilities.",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://deepseek.ai/blog/${slug}`
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Deep Seek AI Blog</title>
        <meta name="description" content="Explore Deep Seek's revolutionary language model with enhanced contextual understanding and improved reasoning capabilities." />
        <link rel="canonical" href={`https://deepseek.ai/blog/${slug}`} />
        
        <meta property="og:title" content={`${post.title} | Deep Seek AI Blog`} />
        <meta property="og:description" content="Explore Deep Seek's revolutionary language model with enhanced contextual understanding and improved reasoning capabilities." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://deepseek.ai/blog/${slug}`} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | Deep Seek AI Blog`} />
        <meta name="twitter:description" content="Explore Deep Seek's revolutionary language model with enhanced contextual understanding and improved reasoning capabilities." />
        
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-8 hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          <NavigationButtons />

          <article className={`transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
            <div className="mb-8">
              <div className="text-gray-500 mb-2">
                {post.date} • {post.author}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
            </div>
            
            <div className="prose prose-lg max-w-none mb-12">
              <p>{post.content}</p>
            </div>

            <div className="mt-12 mb-12 border-t border-b border-gray-200 py-8">
              <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
              <RelatedLinks />
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
              <a
                href="https://chromewebstore.google.com/detail/deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
                aria-label="Get Deep Seek App"
              >
                Try Deep Seek AI Now
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </a>
              <button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#0066FF] text-base font-medium rounded-full text-[#0066FF] bg-white hover:bg-blue-50 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
                aria-label="Create AI Agents"
              >
                Create AI Agents
                <Bot className="ml-2 h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </article>
        </div>
      </div>

      <WaitlistDialog 
        isOpen={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)} 
      />
    </>
  );
};

export default BlogPost;
